// src/components/generics/ImpersonationBanner.jsx
import React, { useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../utils/AxiosInstance";

/**
 * Fixed banner shown on every page while an admin is impersonating another
 * user ("Ver como"). Mounted once near the root (App.jsx) so it stays
 * visible no matter which route the admin lands on inside the impersonated
 * session.
 *
 * Exiting does a full page reload (`window.location.href`) rather than a
 * client-side navigation — deliberately, so no impersonated-user state left
 * over in memory (Redux, component state) can leak into the admin's next
 * screen.
 */
export default function ImpersonationBanner() {
  const user = useSelector((state) => state.user.user);
  const [exiting, setExiting] = useState(false);

  if (!user?.impersonating) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      await axiosInstance.post("/security/auth/exit-impersonation");
    } catch {
      // Even if the request fails, sending the admin back to /admin forces
      // a fresh /logged-user check next load, which is the safest fallback.
    }
    window.location.href = "/admin";
  };

  return (
    <div
      role="status"
      className="fixed top-0 left-0 right-0 z-[300] flex items-center justify-center gap-3 px-4 py-2"
      style={{
        background: "var(--red-dim)",
        borderBottom: "1px solid var(--red-border)",
        backdropFilter: "blur(10px)",
      }}
    >
      <i className="ti ti-user-shield" style={{ fontSize: 16, color: "var(--red)" }} />
      <span className="text-[13px] font-medium" style={{ color: "var(--red)" }}>
        Viewing as {user.name || user.email} — actions you take affect this account.
      </span>
      <button
        type="button"
        onClick={handleExit}
        disabled={exiting}
        className="text-[12.5px] font-semibold rounded-md transition-opacity"
        style={{
          padding: "4px 10px",
          color: "var(--red)",
          border: "1px solid var(--red-border)",
          background: "transparent",
          opacity: exiting ? 0.6 : 1,
        }}
      >
        {exiting ? "Exiting…" : "Exit impersonation"}
      </button>
    </div>
  );
}
